import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, UntypedFormControl, UntypedFormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { INSAutoCompleteGroup, INSAutoCompleteOption } from '@norfolk-southern/accessns-components';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';

import { IThirdPartyEid } from '@ruby/modules/third-party/models/third-party.interface';
import { IStation } from '@ruby/shared/models/commons/station.interface';

@Component({
  selector: 'ng-third-party-request-eid',
  templateUrl: './third-party-request-eid.dialog.html',
  styleUrls: ['./third-party-request-eid.dialog.scss']
})
export class ThirdPartyRequestEidDialogComponent implements OnInit, OnDestroy, OnChanges {
  @Input() states!: Array<INSAutoCompleteGroup>;
  @Input() destinationStations!: Array<INSAutoCompleteOption>;
  @Input() isLoadingDS!: boolean;
  @Input() isLoadingStcc!: boolean;
  @Input() stccList!: Array<INSAutoCompleteOption>;
  @Output() loadOriginStationsData = new EventEmitter<IStation>();
  @Output() request = new EventEmitter<IThirdPartyEid>();
  @Output() loadStccData = new EventEmitter<string>();
  @Output() clearStcc = new EventEmitter();
  eidForm: UntypedFormGroup = new UntypedFormGroup({
    officialCustomer: new UntypedFormControl('', [Validators.required, this.validator()]),
    address: new UntypedFormControl('', Validators.required),
    stcc: new UntypedFormControl('', [Validators.required, this.autocompleteValidator('stcc')]),
    zip: new UntypedFormControl('', Validators.required),
    state: new UntypedFormControl('', [Validators.required, this.autocompleteValidator()]),
    city: new UntypedFormControl('', [Validators.required, this.autocompleteValidator()]),
    differentAddress: new UntypedFormControl(false),
    otherAddress: new UntypedFormControl(''),
    otherZip: new UntypedFormControl(''),
    otherState: new UntypedFormControl(''),
    otherCity: new UntypedFormControl(''),
    parentCompany: new UntypedFormControl(''),
    dunsNumber: new UntypedFormControl(''),
    parentCompanyAddress: new UntypedFormControl(''),
    parentZip: new UntypedFormControl(''),
    parentState: new UntypedFormControl('', [this.autocompleteValidator()]),
    parentCity: new UntypedFormControl('', [this.autocompleteValidator()]),
    thirdPartyProvider: new UntypedFormControl(false)
  });
  debounceTimeout = 200;
  isLoadingCity = false;
  isLoadingOtherCity = false;
  isLoadingParentCity = false;
  isParentAddressRequired = false;
  pattern = /^[a-zA-Z&.,\-' ]+(\s{0,1}[a-zA-Z&.,\-' ])*$/;
  private subscriptionNotifier = new Subject<void>();

  constructor() { }

  ngOnInit(): void {
    this.setup();
  }

  ngOnDestroy(): void {
    this.subscriptionNotifier.next();
    this.subscriptionNotifier.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.isLoadingDS && !changes.isLoadingDS.currentValue) {
      this.isLoadingCity = false;
      this.isLoadingOtherCity = false;
      this.isLoadingParentCity = false;
    }
  }

  /**
   * Submit
   *
   * @summary Method Submits the form data to request new EID
   * @returns void
   */
  submit(): void {
    const data: IThirdPartyEid = {
      address: {
        address: this.eidForm.controls.address.value,
        city: this.eidForm.controls.city.value.value,
        state: this.eidForm.controls.state.value.key,
        zip: this.eidForm.controls.zip.value
      },
      commodity: this.eidForm.controls.stcc.value.value,
      dunsNumber: this.eidForm.controls.dunsNumber.value,
      name: this.eidForm.controls.officialCustomer.value,
      parentCompanyName: this.eidForm.controls.parentCompany.value,
      sameMailAddress: this.eidForm.controls.differentAddress.value,
      thirdParty: this.eidForm.controls.thirdPartyProvider.value
    };

    if (data.sameMailAddress) {
      data.mailAddress = {
        address: this.eidForm.controls.otherAddress.value,
        city: this.eidForm.controls.otherCity.value.value,
        state: this.eidForm.controls.otherState.value.key,
        zip: this.eidForm.controls.otherZip.value
      };
    }

    if (this.eidForm.controls.parentCompanyAddress.value &&
      this.eidForm.controls.parentCity.value.value &&
      this.eidForm.controls.parentState.value.key &&
      this.eidForm.controls.parentZip.value) {
      data.parentCompanyAddressDTO = {
        address: this.eidForm.controls.parentCompanyAddress.value,
        city: this.eidForm.controls.parentCity.value.value,
        state: this.eidForm.controls.parentState.value.key,
        zip: this.eidForm.controls.parentZip.value
      };
    }
    this.request.emit(data);
  }

  /**
   * Setup
   *
   * @summary Method Setups the form listeners
   * @returns void
   */
  setup(): void {
    this.eidForm.controls.state.valueChanges.pipe(
      debounceTime(this.debounceTimeout),
      distinctUntilChanged(),
      takeUntil(this.subscriptionNotifier)
    ).subscribe((state: string | INSAutoCompleteOption) => {
      if (!state || typeof state === 'string') {
        this.eidForm.controls.city.reset();
        this.eidForm.controls.city.disable();
      }
    });

    this.eidForm.controls.city.valueChanges.pipe(
      debounceTime(this.debounceTimeout),
      distinctUntilChanged(),
      takeUntil(this.subscriptionNotifier))
      .subscribe((stationName: string) => {
        if (stationName && stationName.length > 2) {
          this.loadOriginStationsData.emit({
            stateAbbreviation: this.eidForm.controls.state.value.key,
            station: stationName
          });
          this.isLoadingCity = true;
        }
      });

    this.eidForm.controls.parentState.valueChanges.pipe(
      debounceTime(this.debounceTimeout),
      distinctUntilChanged(),
      takeUntil(this.subscriptionNotifier)
    ).subscribe((state: string | INSAutoCompleteOption) => {
      if (!state || typeof state === 'string') {
        this.eidForm.controls.parentCity.reset();
        this.eidForm.controls.parentCity.disable();
      }
    });

    this.eidForm.controls.parentCity.valueChanges.pipe(
      debounceTime(this.debounceTimeout),
      distinctUntilChanged(),
      takeUntil(this.subscriptionNotifier))
      .subscribe((stationName: string) => {
        if (stationName && stationName.length > 2) {
          this.loadOriginStationsData.emit({
            stateAbbreviation: this.eidForm.controls.parentState.value.key,
            station: stationName
          });
          this.isLoadingParentCity = true;
        }
      });

    this.eidForm.controls.stcc.valueChanges.pipe(
      debounceTime(this.debounceTimeout),
      distinctUntilChanged(),
      takeUntil(this.subscriptionNotifier)
    ).subscribe((stcc: string | INSAutoCompleteOption) => {
      if (typeof stcc === 'string') {
        if (stcc.length > 2) {
          this.loadStccData.emit(stcc.toUpperCase());
        } else {
          this.clearStcc.emit();
        }
      }
    });

    this.eidForm.controls.parentCompanyAddress.valueChanges.pipe(
      debounceTime(this.debounceTimeout),
      distinctUntilChanged(),
      takeUntil(this.subscriptionNotifier)
    ).subscribe((parentCompanyAddress: string) => {
      if (parentCompanyAddress.trim().length > 0) {
        this.eidForm.controls.parentCompanyAddress.setValidators(Validators.required);
        this.eidForm.controls.parentCity.setValidators(Validators.required);
        this.eidForm.controls.parentState.setValidators(Validators.required);
        this.eidForm.controls.parentZip.setValidators(Validators.required);
        this.isParentAddressRequired = true;
      } else {
        this.eidForm.controls.parentCompanyAddress.clearValidators();
        this.eidForm.controls.parentCompanyAddress.reset('');
        this.eidForm.controls.parentCity.clearValidators();
        this.eidForm.controls.parentCity.reset('');
        this.eidForm.controls.parentState.clearValidators();
        this.eidForm.controls.parentState.reset('');
        this.eidForm.controls.parentZip.clearValidators();
        this.eidForm.controls.parentZip.reset('');
        this.isParentAddressRequired = false;
      }
      this.eidForm.controls.parentCompanyAddress.updateValueAndValidity();
      this.eidForm.controls.parentCity.updateValueAndValidity();
      this.eidForm.controls.parentState.updateValueAndValidity();
      this.eidForm.controls.parentZip.updateValueAndValidity();
      this.eidForm.updateValueAndValidity();
    });
  }

  /**
   * Handle Mail Address Form
   *
   * @summary Method Setups the form listeners
   * @param $event: MatSlideToggleChange
   * @returns void
   */
  handleMailAddressForm($event: MatSlideToggleChange): void {
    this.handleMailAddressValidators($event.checked);
    if ($event.checked) {
      this.eidForm.controls.otherState.valueChanges.pipe(
        debounceTime(this.debounceTimeout),
        distinctUntilChanged(),
        takeUntil(this.subscriptionNotifier)
      ).subscribe((state: string | INSAutoCompleteOption) => {
        if (!state || typeof state === 'string') {
          this.eidForm.controls.otherCity.reset();
          this.eidForm.controls.otherCity.disable();
        }
      });
      this.eidForm.controls.otherCity.valueChanges.pipe(
        debounceTime(this.debounceTimeout),
        distinctUntilChanged(),
        takeUntil(this.subscriptionNotifier))
        .subscribe((stationName: string) => {
          if (stationName && stationName.length > 2) {
            this.loadOriginStationsData.emit({
              stateAbbreviation: this.eidForm.controls.otherState.value.key,
              station: stationName
            });
            this.isLoadingOtherCity = true;
          }
        });
    }
  }

  /**
   * Set Third Party
   *
   * @summary Method set to true the third party option
   * @param $event: MatSlideToggleChange
   * @returns void
   */
  setThirdParty($event: MatSlideToggleChange): void {
    this.eidForm.controls.thirdPartyProvider.setValue($event.checked);
  }

  /**
   * Autocomplete Selected
   *
   * @summary Method called when autocomplete is selected
   * @param formCtrlName: string
   * @param previousCtrlName: string
   * @returns void
   */
  autocompleteSelected(formCtrlName: string, previousCtrlName: string): void {
    if (typeof this.eidForm.get(previousCtrlName)?.value !== 'string') {
      this.eidForm.get(formCtrlName)?.enable();
    }
  }

  /**
   * Autocomplete Validator
   *
   * @summary Autocomplete Validator
   * @param controlName?: string
   * @returns ValidatorFn
   */
  autocompleteValidator(controlName?: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors => {
      let result = {};
      if (typeof control.value === 'string' && control.value !== '') {
        result = { invalidSelection: { value: control.value } };
      }

      if (controlName === 'stcc' && typeof control.value === 'string' && control.value.length > 7) {
        return { maxlength: { requiredLength: 7 } };
      }

      return result;
    };
  }

  /**
   * Validator
   *
   * @summary Autocomplete Validator
   * @returns ValidatorFn
   */
  validator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors => {
      let result = {};
      if (typeof control.value === 'string' && control.value.trim().length < 1) {
        result = { invalid: { value: control.value } };
      }
      return result;
    };
  }

  private handleMailAddressValidators(isAdd: boolean): void {
    if (isAdd) {
      this.eidForm.controls.otherState.setValidators([Validators.required, this.autocompleteValidator()]);
      this.eidForm.controls.otherCity.setValidators([Validators.required, this.autocompleteValidator()]);
      this.eidForm.controls.otherAddress.setValidators(Validators.required);
      this.eidForm.controls.otherZip.setValidators(Validators.required);
    } else {
      this.eidForm.controls.otherState.clearValidators();
      this.eidForm.controls.otherCity.clearValidators();
      this.eidForm.controls.otherAddress.clearValidators();
      this.eidForm.controls.otherZip.clearValidators();
    }
    this.eidForm.controls.otherCity.updateValueAndValidity();
    this.eidForm.controls.otherState.updateValueAndValidity();
    this.eidForm.controls.otherAddress.updateValueAndValidity();
    this.eidForm.controls.otherZip.updateValueAndValidity();
    this.eidForm.updateValueAndValidity();
  }
}
